// src/App.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import confetti from "canvas-confetti";
import "./App.css";
import { HOGWASH_SETS, HOGWASH_SCHEDULE } from "./hogwashSets";
import { supabase } from './supabaseClient';
import { getPlayerId } from './player';


import rankDiamond from "./assets/diamond-hog.png";
import rankGolden from "./assets/golden-boar.png";
import rankSilver from "./assets/silver-snout.png";
import rankBronze from "./assets/bronze-rump.png";
import rankHogwashed from "./assets/hogwashed.png";
import peakingPig from "./assets/Peaking-pig.png";

const MAX_ATTEMPTS = 4;
const MUST_SELECT = 3;

const THEME = {
  pageBg: "#1A2433",
  panel: "#263246",
  accent: "#E17D66",
  warm: "#E5C99F",
  text: "#FFFFFF",
  subtext: "#C7D0DE",
  good: "#34C759",
  ok: "#8BCBFF",
};

function shuffle(arr, seed) {
  const a = [...arr];
  let s = seed ?? Math.floor(Math.random() * 1e9);
  function rand() {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  }
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function getGameDayKey() {
  const now = new Date();
  
  // Convert to Eastern Time
  const isDST = (date) => {
    const jan = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();
    const jul = new Date(date.getFullYear(), 6, 1).getTimezoneOffset();
    return Math.max(jan, jul) !== date.getTimezoneOffset();
  };
  
  const offset = isDST(now) ? -4 : -5; // Adjust for daylight saving time
  const etTime = new Date(now.getTime() + (offset * 60 * 60 * 1000));
  
  // If it's before 1 AM ET, use the previous day
  if (etTime.getHours() < 1) {
    etTime.setDate(etTime.getDate() - 1);
  }
  
  // Return date string in YYYY-MM-DD format
  return etTime.toISOString().split('T')[0];
}

function pickDailySet(sets) {
  const gameDay = getGameDayKey();
  const epochDay = Math.floor(new Date(gameDay).getTime() / (24 * 60 * 60 * 1000));

  // Build a lookup map so removal of a set from HOGWASH_SETS is graceful.
  const setMap = Object.fromEntries(sets.map((s) => [s.id, s]));

  // Filter schedule to only IDs that still exist (handles removed sets).
  const activeSchedule = HOGWASH_SCHEDULE.filter((id) => setMap[id]);

  const dayInCycle = epochDay % activeSchedule.length;
  return setMap[activeSchedule[dayInCycle]];
}

function triggerWinConfetti() {
  const defaults = {
    origin: { y: 0.88 },
    spread: 90,
    startVelocity: 30,
    gravity: 1.2,
    ticks: 70,
    shapes: ["square", "circle"],
    scalar: 1.2,
    zIndex: 9999,
  };

  const colors = [THEME.accent, THEME.warm, THEME.ok, "#FFCC00", THEME.good];

  for (let i = 0; i < 5; i++) {
    setTimeout(() => {
      confetti({
        ...defaults,
        particleCount: 90,
        origin: { x: 0.15 + i * 0.18, y: 0.88 },
        colors,
      });
    }, i * 160);
  }

  setTimeout(() => {
    confetti({
      ...defaults,
      particleCount: 150,
      angle: 60,
      origin: { x: 0, y: 0.88 },
      colors,
    });
    confetti({
      ...defaults,
      particleCount: 150,
      angle: 120,
      origin: { x: 1, y: 0.88 },
      colors,
    });
  }, 900);
}

function getRank(attemptsUsed) {
  switch (attemptsUsed) {
    case 1:
      return { title: "Diamond Hog", img: rankDiamond, line: "Flawless instincts. Absolute hogwash mastery." };
    case 2:
      return { title: "Golden Boar", img: rankGolden, line: "Cleared the boar’d. — worth its weight in gold." };
    case 3:
      return { title: "Silver Snout", img: rankSilver, line: "A few laps around the pen, but the hog came home. Congrats!" };
    case 4:
      return { title: "Bronze Rump", img: rankBronze, line: "Last to arrive… straight to the rump." };
      default:
      return null;
  }
}

function makePigMeter(count) {
  const c = Math.max(0, Math.min(3, count || 0));
  return Array.from({ length: 3 }).map((_, i) => ({ on: i < c }));
}

function buildShareText({ gameName, puzzleId, statusLabel, attemptsUsed, history, won }) {
  const rows = history.map((h) =>
    "🐷".repeat(h.correctCount) + "❌".repeat(3 - h.correctCount)
  );
  const resultLine = won
    ? `Attempts: ${attemptsUsed} — ${statusLabel}`
    : `Today I got hogwashed! 🐷`;
  const header = `${gameName} 🐷\nToday's theme: ${puzzleId}\n${resultLine}\n`;
  const body = rows.length ? `\n${rows.join("\n")}\n` : "\n";
  const footer = `\nPlay: https://oinkoinkgames.com/#hogwash`;
  return header + body + footer;
}
 
function LifetimeStatsPanel({ statsLoading, lifetimeStats, theme: T }) {
  return (
    <div style={{
      marginTop: '2rem',
      padding: '1.5rem',
      background: T.panel,
      borderRadius: '12px',
      border: `2px solid ${T.warm}`,
    }}>
      <h3 style={{ marginTop: 0, color: T.warm, marginBottom: '1rem', textAlign: 'center' }}>
        Your Lifetime Stats
      </h3>

      {statsLoading && (
        <p style={{ margin: 0, color: T.subtext, textAlign: 'center' }}>Loading stats…</p>
      )}

      {!statsLoading && lifetimeStats && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem',
          }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: 0, color: T.subtext, fontSize: '0.9rem' }}>Games Played</p>
              <p style={{ margin: 0, fontSize: '1.8rem', fontWeight: 'bold', color: T.text }}>
                {lifetimeStats.gamesPlayed}
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: 0, color: T.subtext, fontSize: '0.9rem' }}>Avg Attempts</p>
              <p style={{ margin: 0, fontSize: '1.8rem', fontWeight: 'bold', color: T.text }}>
                {lifetimeStats.avgAttempts.toFixed(1)}
              </p>
            </div>
          </div>

          <div style={{ borderTop: `1px solid ${T.subtext}33`, paddingTop: '1rem' }}>
            <p style={{ margin: '0 0 0.75rem 0', color: T.warm, fontSize: '0.95rem', fontWeight: 'bold' }}>
              Rank Breakdown
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {[
                { label: '💎 Diamond', value: lifetimeStats.diamondHogs },
                { label: '🥇 Gold',    value: lifetimeStats.goldenBoars },
                { label: '🥈 Silver',  value: lifetimeStats.silverSnouts },
                { label: '🥉 Bronze',  value: lifetimeStats.bronzeRumps },
                { label: '❌ Hogwashed', value: lifetimeStats.hogwashed },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: T.subtext }}>{label}</span>
                  <span style={{ fontWeight: 'bold', color: T.text }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {!statsLoading && !lifetimeStats && (
        <p style={{ margin: 0, color: T.subtext, textAlign: 'center' }}>
          No stats yet — this is your first game!
        </p>
      )}
    </div>
  );
}

export default function App() {
  const dailySet = useMemo(() => pickDailySet(HOGWASH_SETS), []);
  const [items] = useState(() => shuffle(dailySet.items));
  const [playerId] = useState(() => getPlayerId());
  const [isLoadingGameState, setIsLoadingGameState] = useState(false);
  
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [attempt, setAttempt] = useState(0);
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState("playing");
  const [lastResult, setLastResult] = useState(null);
  const [snort, setSnort] = useState(false);
  const [resultsIn, setResultsIn] = useState(false);
  const [lifetimeStats, setLifetimeStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showStats, setShowStats] = useState(false);

  const snortTimerRef = useRef(null);
  const hogwashIds = useMemo(() => new Set(items.filter((x) => x.hogwash).map((x) => x.id)), [items]);

  // Preload rank images
useEffect(() => {
  [rankDiamond, rankGolden, rankSilver, rankBronze, rankHogwashed].forEach((src) => {
    const img = new Image();
    img.src = src;
  });
}, []);

const checkTodaysGame = async () => {
  if (!playerId) {
    return;
  }
  
  setIsLoadingGameState(true); // Show loading only when actually checking
  
  const gameDay = getGameDayKey();
  
  const { data, error } = await supabase
    .from('hogwash_runs')
    .select('id, won, attempts_used')
    .eq('player_id', playerId)
    .eq('game_day', gameDay)
    .single();
  
  if (error && error.code !== 'PGRST116') {
    console.error('Error checking today\'s game:', error);
    setIsLoadingGameState(false);
    return;
  }
  
  if (data) {
    const { data: attemptsData, error: attemptsError } = await supabase
      .from('hogwash_attempts')
      .select('attempt_number, correct_count')
      .eq('run_id', data.id)
      .order('attempt_number');
    
    if (!attemptsError && attemptsData) {
      const restoredHistory = attemptsData.map(a => ({
        correctCount: a.correct_count
      }));
      
      setHistory(restoredHistory);
      setAttempt(data.attempts_used);
      setStatus(data.won ? 'won' : 'lost');
    }
  }
  
  setIsLoadingGameState(false);
};

const saveGameToSupabase = async (won, attemptsUsed, gameHistory) => {
  if (!playerId) return;
  
  const gameDay = getGameDayKey();
  
  // Step 1: Ensure player exists
  const { error: playerError } = await supabase
    .from('players')
    .upsert(
      { 
        id: playerId,
        last_played: new Date().toISOString()
      },
      { 
        onConflict: 'id'
      }
    );
  
  if (playerError) {
    console.error('Error upserting player:', playerError);
    return;
  }
  
  // Step 2: Insert game run
  const { data: runRow, error: runErr } = await supabase
    .from('hogwash_runs')
    .insert([{
      player_id: playerId,
      game_day: gameDay,
      theme_id: dailySet.id,
      won: won,
      attempts_used: attemptsUsed,
    }])
    .select('id')
    .single();
  
  if (runErr) {
    console.error('Error inserting hogwash_runs:', runErr);
    return;
  }
  
  // Step 3: Insert attempts
  const inserts = gameHistory.map((h, idx) => ({
    run_id: runRow.id,
    player_id: playerId,
    game_day: gameDay,
    attempt_number: idx + 1,
    correct_count: h.correctCount,
  }));
  
  const { error: attemptsErr } = await supabase
    .from('hogwash_attempts')
    .insert(inserts);
  
  if (attemptsErr) console.error('Error inserting hogwash_attempts:', attemptsErr);
};

const fetchLifetimeStats = async () => {
  if (!playerId) return;

  setStatsLoading(true);

  // Fetch all game runs for this player
  const { data: runs, error: runsError } = await supabase
    .from('hogwash_runs')
    .select('won, attempts_used')
    .eq('player_id', playerId);

  if (runsError) {
    console.error('Error loading hogwash_runs:', runsError);
    setStatsLoading(false);
    return;
  }

  if (!runs || runs.length === 0) {
    setStatsLoading(false);
    return;
  }

  // Calculate stats
  const totalGames = runs.length;
  const totalAttempts = runs.reduce((sum, r) => sum + r.attempts_used, 0);
  const avgAttempts = totalAttempts / totalGames;

  // Count each rank
  const diamondHogs = runs.filter(r => r.won && r.attempts_used === 1).length;
  const goldenBoars = runs.filter(r => r.won && r.attempts_used === 2).length;
  const silverSnouts = runs.filter(r => r.won && r.attempts_used === 3).length;
  const bronzeRumps = runs.filter(r => r.won && r.attempts_used === 4).length;
  const hogwashed = runs.filter(r => !r.won).length;

  setLifetimeStats({
    avgAttempts,
    diamondHogs,
    goldenBoars,
    silverSnouts,
    bronzeRumps,
    hogwashed,
    gamesPlayed: totalGames,
  });

  setStatsLoading(false);
};

  const attemptsLeft = MAX_ATTEMPTS - attempt;
  const canSubmit = status === "playing" && selectedIds.size === MUST_SELECT;

  // ✅ Run results entrance on BOTH "won" and "lost"
  useEffect(() => {
    if (status !== "won" && status !== "lost") {
      setResultsIn(false);
      return;
    }
    const t = setTimeout(() => setResultsIn(true), 50);
    return () => clearTimeout(t);
  }, [status]);

  useEffect(() => {
    checkTodaysGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);


  function toggle(id) {
    if (status !== "playing") return;

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        if (next.size >= MUST_SELECT) return next;
        next.add(id);
      }
      return next;
    });
  }

  function triggerSnort() {
    setSnort(true);
    if (snortTimerRef.current) clearTimeout(snortTimerRef.current);
    snortTimerRef.current = setTimeout(() => setSnort(false), 520);
  }

  async function submit() {
    if (!canSubmit) return;
  
    const picks = Array.from(selectedIds);
    const correctCount = picks.reduce((acc, id) => acc + (hogwashIds.has(id) ? 1 : 0), 0);
  
    triggerSnort();
  
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    setLastResult({ correctCount });
    
    const updatedHistory = [...history, { correctCount }];
    setHistory(updatedHistory);
  
    if (correctCount === MUST_SELECT) {
      setStatus("won");
      // Save to database
      await saveGameToSupabase(true, nextAttempt, updatedHistory);
      // Confetti ONLY ON WIN
      setTimeout(() => triggerWinConfetti(), 220);
      return;
    }
  
    if (nextAttempt >= MAX_ATTEMPTS) {
      setStatus("lost");
      // Save to database
      await saveGameToSupabase(false, nextAttempt, updatedHistory);
      return;
    }
  
    setSelectedIds(new Set());
  }

  function copyToClipboard(text) {
    if (!document.hasFocus()) {
      alert("Click the window first, then try share again.");
      return;
    }

    navigator.clipboard
      .writeText(text)
      .then(() => {
        const toast = document.createElement("div");
        toast.innerText = "Results copied to clipboard!";
        toast.style.position = "fixed";
        toast.style.bottom = "20px";
        toast.style.left = "50%";
        toast.style.transform = "translateX(-50%)";
        toast.style.backgroundColor = THEME.ok;
        toast.style.color = "#000";
        toast.style.padding = "10px 20px";
        toast.style.borderRadius = "8px";
        toast.style.zIndex = "9999";
        toast.style.boxShadow = "0 2px 10px rgba(0,0,0,0.3)";
        toast.style.transition = "opacity 0.3s ease-in-out";
        document.body.appendChild(toast);

        setTimeout(() => {
          toast.style.opacity = "0";
          setTimeout(() => document.body.removeChild(toast), 300);
        }, 2000);
      })
      .catch(() => alert("Failed to copy results. Please try again."));
  }

  async function handleShare() {
    const won = status === "won";
    const attemptsUsed = attempt;
    const bestTry = history.reduce((m, h) => Math.max(m, h.correctCount), 0);

    let statusLabel = "YOU GOT HOGWASHED!";
    if (won) statusLabel = getRank(attemptsUsed).title.toUpperCase();
    else statusLabel = `BEST TRY: ${"🐷".repeat(bestTry)}${"▫️".repeat(3 - bestTry)}`;

    const shareText = buildShareText({
      gameName: "Hogwash",
      puzzleId: dailySet.id,
      statusLabel,
      attemptsUsed,
      history,
      won,
    });

    if (navigator.share) {
      try {
        await navigator.share({ title: "Hogwash Results", text: shareText });
      } catch {
        copyToClipboard(shareText);
      }
    } else {
      copyToClipboard(shareText);
    }
  }

  const pigMeter = (count) => {
    const meters = makePigMeter(count);
    return (
      <div className="hw-meter" aria-label={`Hogwash meter: ${count} of 3`}>
        {meters.map((m, i) => (
          <span key={i} className={`hw-meter-pig ${m.on ? "on" : ""}`}>
            🐷
          </span>
        ))}
      </div>
    );
  };

  const summaryRow = (correctCount) => (
    <div className="hw-summary-row">
      <div className="hw-summary-left">
        {pigMeter(correctCount)}
        <div className="hw-summary-text">
          <div className="hw-summary-big">{correctCount}/3 hogwash found</div>
          <div className="hw-summary-sub">Tweak your picks and try again.</div>
        </div>
      </div>

      <div className="hw-attempts-text" aria-label="Attempts left">
        Attempts left: <span className="hw-attempts-numtext">{attemptsLeft}</span>
      </div>
    </div>
  );

  if (isLoadingGameState) {
    return (
      <div className="hw-page hw-solid">
        <div className="hw-shell" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <p style={{ fontSize: '1.2rem', color: THEME.text }}>Loading...</p>
        </div>
      </div>
    );
  }

  // ✅ RESULTS SCREEN
  if (status === "won" || status === "lost") {
    const won = status === "won";
    const used = attempt;
    const best = history.reduce((m, h) => Math.max(m, h.correctCount), 0);
    const rank = won ? getRank(used) : null;

    return (
      <div className="hw-page hw-solid">
        <div className="hw-shell">
          <div className="hw-topbar">
            <div className="hw-brand">HOGWASH</div>
          </div>

          <div className="hw-final">
            <div
              className={[
                "hw-final-card",
                resultsIn ? "show" : "",
                won ? "win" : "loss",
              ].join(" ")}
            >
              {won ? (
                <>
                  <img
                    className={[
                      "hw-rank-img",
                      resultsIn ? "show" : "",
                      "win",
                    ].join(" ")}
                    src={rank.img}
                    alt={rank.title}
                    draggable="false"
                  />

                  <h1 className="hw-final-title">{rank.title}</h1>
                  <div className="hw-final-sub">{rank.line}</div>

                  <div className="hw-final-stats">
                    <div className="hw-stat">
                      <div className="hw-stat-label">Attempts</div>
                      <div className="hw-stat-value">
                        {used}/{MAX_ATTEMPTS}
                      </div>
                    </div>
                    <div className="hw-stat">
                      <div className="hw-stat-label">Theme</div>
                      <div className="hw-stat-value">{dailySet.themeLabel}</div>
                    </div>
                  </div>

                  <div className="hw-final-actions">
                    <button className="hw-primary" onClick={handleShare}>
                      Share Results
                    </button>
                    <button
                      className="hw-secondary"
                      onClick={() => { setShowStats(true); fetchLifetimeStats(); }}
                      style={{ marginTop: '0.75rem' }}
                    >
                      View Lifetime Stats
                    </button>
                  </div>

                  {showStats && <LifetimeStatsPanel statsLoading={statsLoading} lifetimeStats={lifetimeStats} theme={THEME} />}
                </>
              ) : (
                <>
                  <img
                    className={[
                      "hw-rank-img",
                      resultsIn ? "show" : "",
                      "loss",
                    ].join(" ")}
                    src={rankHogwashed}
                    alt="You Got Hogwashed!"
                    draggable="false"
                  />

                  <h1 className="hw-final-title">You Got Hogwashed!</h1>
                  <div className="hw-final-sub">Slopped at the finish line! Better luck tomorrow.</div>

                  <div className="hw-final-stats">
                    <div className="hw-stat">
                      <div className="hw-stat-label">Best try</div>
                      <div className="hw-stat-value hw-stat-pigs">{pigMeter(best)}</div>
                    </div>
                    <div className="hw-stat">
                      <div className="hw-stat-label">Theme</div>
                      <div className="hw-stat-value">{dailySet.themeLabel}</div>
                    </div>
                  </div>

                  <div className="hw-final-actions">
                    <button className="hw-primary" onClick={handleShare}>
                      Share Results
                    </button>
                    <button
                      className="hw-secondary"
                      onClick={() => { setShowStats(true); fetchLifetimeStats(); }}
                      style={{ marginTop: '0.75rem' }}
                    >
                      View Lifetime Stats
                    </button>
                  </div>

                  {showStats && <LifetimeStatsPanel statsLoading={statsLoading} lifetimeStats={lifetimeStats} theme={THEME} />}
                </>
              )}
            </div>
          </div>

          {/* Reveal grid */}
          <section className="hw-grid hw-grid-final" aria-label="Statements grid">
            {items.map((it) => {
              const isHogwash = hogwashIds.has(it.id);
              return (
                <div
                  key={it.id}
                  className={`hw-card ${isHogwash ? "hogwash" : ""}`}
                  aria-label={isHogwash ? "Hogwash item" : "Real item"}
                >
                  <div className="hw-card-text">{it.text}</div>
                </div>
              );
            })}
          </section>

          <div className="hw-reveal-note">{dailySet.revealNote}</div>
        </div>
      </div>
    );
  }

  // ✅ PLAYING SCREEN
  return (
    <div className="hw-page hw-solid">
      <div className="hw-shell">
        <div className="hw-topbar">
          <div className="hw-brand">HOGWASH</div>
          <div className="hw-attempts-text" aria-label="Attempts left">
            Attempts left: <span className="hw-attempts-numtext">{attemptsLeft}</span>
          </div>
        </div>

        <div className="hw-peaking-pig-wrapper">
          <img src={peakingPig} alt="Peaking pig" className="hw-peaking-pig" draggable="false" />
        </div>

        <header className="hw-header">
          <div className="hw-instruction">Today's theme 
          </div>
          <div className="hw-theme"> {dailySet.themeLabel}</div>
          <div className="hw-helper"><span className="hw-instruction-pig">Select the 3 items which are hogwash (false).</span></div>
        </header>

        <section className="hw-grid" aria-label="Statements grid">
          {items.map((it) => {
            const isSelected = selectedIds.has(it.id);
            return (
              <button
                type="button"
                key={it.id}
                className={["hw-card", isSelected ? "selected" : "", snort && isSelected ? "snort" : ""]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => toggle(it.id)}
                disabled={status !== "playing"}
                aria-pressed={isSelected}
              >
                <div className="hw-card-text">{it.text}</div>
              </button>
            );
          })}
        </section>

        <section className="hw-controls hw-controls-sticky">
          <button
            className="hw-primary"
            onClick={submit}
            disabled={!canSubmit}
            aria-disabled={!canSubmit}
            title={!canSubmit ? "Select exactly 3 items" : "Submit guess"}
          >
            Submit
          </button>

          {!canSubmit && <div className="hw-hint">Select {MUST_SELECT} items to submit.</div>}
        </section>

        <section className="hw-feedback">
          {lastResult && (
            <div className={`hw-feedback-box ${snort ? "snort" : ""}`}>
              {summaryRow(lastResult.correctCount)}
            </div>
          )}
        </section>

        {history.length > 0 && (
          <section className="hw-history hw-history-compact">
            <div className="hw-history-title">Attempts</div>
            <div className="hw-history-rows">
              {history.map((h, idx) => (
                <div className="hw-history-row" key={idx}>
                  <div className="hw-attempt">#{idx + 1}</div>
                  <div className="hw-history-pigs">{pigMeter(h.correctCount)}</div>
                  <div className="hw-history-text">{h.correctCount}/3</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
